import React, { useEffect, useMemo, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "../../../../toast";
import { IoMdAdd, IoMdArrowRoundBack, IoMdCreate, IoMdTrash } from "react-icons/io";
import { useParams } from "react-router-dom";
import AdminLayout from "../../Layout";
import ConformationModal from "../../Components/ConformationModal.jsx";

const GET_ALL_RANGE_LEVELS = gql`
  mutation GetAllRangeLevels {
    getAllRangeLevels
  }
`;

const GET_MESSAGES_BY_RANGE = gql`
  query GetMessagesByRange($rangeLabel: String!, $campusName: String) {
    getMessagesByRangeLevel(rangeLabel: $rangeLabel, campusName: $campusName) {
      campusName
      message
      greeting
    }
  }
`;

const CREATE_RANGE_LEVEL = gql`
  mutation CreateRangeLevel($input: CreateRangeLevelInput!) {
    createRangeLevel(createRangeLevelInput: $input) {
      id
      ranges {
        rangeLabel
      }
      updatedAt
    }
  }
`;

const UPDATE_RANGE_LEVEL = gql`
  mutation UpdateRangeLevel($input: UpdateRangeLevelInput!) {
    updateRangeLevel(updateRangeLevelInput: $input) {
      id
      ranges {
        rangeLabel
      }
      updatedAt
    }
  }
`;

const UPSERT_MESSAGE_IN_RANGE = gql`
  mutation UpsertMessageInRange($input: UpsertMessageInRangeInput!) {
    upsertMessageInRange(upsertMessageInRangeInput: $input) {
      id
      ranges {
        rangeLabel
        entries {
          campusName
          message
          greeting
        }
      }
      updatedAt
    }
  }
`;

const DELETE_MESSAGE_IN_RANGE = gql`
  mutation DeleteMessageInRange($input: DeleteMessageInRangeInput!) {
    deleteMessageInRange(deleteMessageInRangeInput: $input) {
      id
      ranges {
        rangeLabel
        entries {
          campusName
          message
          greeting
        }
      }
      updatedAt
    }
  }
`;

const toEntries = (rangeLabel, entries) => {
  const list = Array.isArray(entries) ? entries : [];
  return list.map((entry, idx) => ({
    campusName: entry?.campusName || "",
    message: entry?.message || "",
    greeting: entry?.greeting || "",
    _localKey: `${rangeLabel}-${String(entry?.campusName || "unknown").toLowerCase()}-${idx}`,
  }));
};

const toRangeLabels = (ranges) => {
  return (Array.isArray(ranges) ? ranges : [])
    .map((r) => String(r?.rangeLabel || "").trim())
    .filter(Boolean);
};

const ReportMessage = () => {
  const { campusName } = useParams();

  const [createRangeLevel, { loading: creatingRange }] = useMutation(CREATE_RANGE_LEVEL);
  const [updateRangeLevel, { loading: updatingRange }] = useMutation(UPDATE_RANGE_LEVEL);
  const [upsertMessageInRange, { loading: upserting }] = useMutation(UPSERT_MESSAGE_IN_RANGE);
  const [deleteMessageInRange, { loading: deleting }] = useMutation(DELETE_MESSAGE_IN_RANGE);

  const [getAllRangeLevels, { loading: rangeLevelsLoading }] = useMutation(GET_ALL_RANGE_LEVELS);

  const [fetchMessagesByRange, { loading: messagesLoading }] = useLazyQuery(GET_MESSAGES_BY_RANGE, {
    fetchPolicy: "network-only",
  });

  const [rangeLabels, setRangeLabels] = useState([]);
  const [entriesByRange, setEntriesByRange] = useState({});
  const [activeRangeLabel, setActiveRangeLabel] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [deletingKey, setDeletingKey] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [showRangeForm, setShowRangeForm] = useState(false);
  const [rangeFormMode, setRangeFormMode] = useState("add");
  const [editOldRangeLabel, setEditOldRangeLabel] = useState("");
  const [rangeMin, setRangeMin] = useState("");
  const [rangeMax, setRangeMax] = useState("");

  const isBusy = creatingRange || updatingRange || upserting || deleting || rangeLevelsLoading || messagesLoading;

  useEffect(() => {
    const loadRangeLevels = async () => {
      try {
        const { data } = await getAllRangeLevels();
        const labels = Array.isArray(data?.getAllRangeLevels) ? data.getAllRangeLevels : [];
        setRangeLabels(labels.filter(Boolean));
      } catch (error) {
        toast.error(error?.message || "Failed to fetch range levels.");
      }
    };

    loadRangeLevels();
  }, [getAllRangeLevels]);

  useEffect(() => {
    if (!rangeLabels.length) return;

    const preloadRangeEntriesForCards = async () => {
      try {
        const results = await Promise.all(
          rangeLabels.map((label) =>
            fetchMessagesByRange({
              variables: {
                rangeLabel: label,
                campusName: campusName || null,
              },
            }).then((res) => ({ label, entries: toEntries(label, res?.data?.getMessagesByRangeLevel || []) }))
          )
        );

        setEntriesByRange((prev) => {
          const next = { ...prev };
          results.forEach(({ label, entries }) => {
            next[label] = entries;
          });
          return next;
        });
      } catch (error) {
        toast.error(error?.message || "Failed to fetch range card data.");
      }
    };

    preloadRangeEntriesForCards();
  }, [rangeLabels, campusName, fetchMessagesByRange]);

  const loadRangeEntries = async (label) => {
    if (!label) return;
    try {
      const { data } = await fetchMessagesByRange({
        variables: {
          rangeLabel: label,
          campusName: campusName || null,
        },
      });

      const nextEntries = toEntries(label, data?.getMessagesByRangeLevel || []);
      setEntriesByRange((prev) => ({
        ...prev,
        [label]: nextEntries,
      }));
    } catch (error) {
      toast.error(error?.message || "Failed to fetch messages for range.");
    }
  };

  useEffect(() => {
    if (!activeRangeLabel) return;
    loadRangeEntries(activeRangeLabel);
  }, [activeRangeLabel]);

  const activeEntries = useMemo(() => {
    if (!activeRangeLabel) return [];
    return entriesByRange[activeRangeLabel] || [];
  }, [activeRangeLabel, entriesByRange]);

  const handleDraftChange = (itemKey, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [itemKey]: {
        ...(prev[itemKey] || {}),
        [field]: value,
      },
    }));
  };

  const startEdit = (entry, rangeLabel) => {
    const key = entry._localKey;
    setEditingKey(key);
    setDrafts((prev) => ({
      ...prev,
      [key]: {
        mode: "edit",
        originalCampusName: entry.campusName || "",
        originalMessage: entry.message || "",
        originalGreeting: entry.greeting || "",
        rangeLabel,
        campusName: entry.campusName || campusName || "",
        message: entry.message || "",
        greeting: entry.greeting || "",
      },
    }));
  };

  const cancelEdit = (itemKey) => {
    const isTemp = (entriesByRange[activeRangeLabel] || []).some((e) => e._localKey === itemKey && e._isTemp);
    if (isTemp) {
      setEntriesByRange((prev) => ({
        ...prev,
        [activeRangeLabel]: (prev[activeRangeLabel] || []).filter((e) => e._localKey !== itemKey),
      }));
    }
    setEditingKey("");
  };

  const ensureRangeExists = async (label) => {
    if ((rangeLabels || []).includes(label)) return true;

    const { data } = await createRangeLevel({
      variables: {
        input: {
          rangeLabel: label,
        },
      },
    });

    const labelsFromMutation = toRangeLabels(data?.createRangeLevel?.ranges);
    if (labelsFromMutation.length) {
      setRangeLabels(labelsFromMutation);
      return true;
    }

    return false;
  };

  const parseRangeLabel = (label) => {
    const match = String(label || "").match(/(\d+)\s*%\s*to\s*(\d+)\s*%/i);
    if (!match) return null;
    return {
      min: match[1],
      max: match[2],
    };
  };

  const openAddRangeForm = () => {
    setRangeFormMode("add");
    setEditOldRangeLabel("");
    setRangeMin("");
    setRangeMax("");
    setShowRangeForm(true);
  };

  const openEditRangeForm = (label) => {
    const parsed = parseRangeLabel(label);
    if (!parsed) {
      toast.error("Range format is invalid. Expected like 0% to 30%.");
      return;
    }

    setRangeFormMode("edit");
    setEditOldRangeLabel(label);
    setRangeMin(parsed.min);
    setRangeMax(parsed.max);
    setShowRangeForm(true);
  };

  const handleSubmitRangeForm = async () => {
    const min = Number(rangeMin);
    const max = Number(rangeMax);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      toast.error("Enter valid min and max values.");
      return;
    }

    if (min < 0 || max > 100 || min > max) {
      toast.error("Range must be between 0 and 100, and min must be <= max.");
      return;
    }

    const newLabel = `${min}% to ${max}%`;

    if (
      rangeFormMode === "add" &&
      rangeLabels.some((label) => String(label).toLowerCase() === newLabel.toLowerCase())
    ) {
      toast.error("Range already exists.");
      return;
    }

    try {
      if (rangeFormMode === "add") {
        const { data } = await createRangeLevel({
          variables: {
            input: {
              rangeLabel: newLabel,
            },
          },
        });

        const labelsFromMutation = toRangeLabels(data?.createRangeLevel?.ranges);
        if (labelsFromMutation.length) {
          setRangeLabels(labelsFromMutation);
        } else {
          setRangeLabels((prev) => [...prev, newLabel]);
        }
      } else {
        if (!editOldRangeLabel) {
          toast.error("Missing old range label.");
          return;
        }

        const { data } = await updateRangeLevel({
          variables: {
            input: {
              oldRangeLabel: editOldRangeLabel,
              newRangeLabel: newLabel,
            },
          },
        });

        const labelsFromMutation = toRangeLabels(data?.updateRangeLevel?.ranges);
        if (labelsFromMutation.length) {
          setRangeLabels(labelsFromMutation);
        } else {
          setRangeLabels((prev) =>
            prev.map((label) => (label === editOldRangeLabel ? newLabel : label))
          );
        }

        setEntriesByRange((prev) => {
          if (!Object.prototype.hasOwnProperty.call(prev, editOldRangeLabel)) return prev;
          const next = { ...prev };
          next[newLabel] = prev[editOldRangeLabel];
          delete next[editOldRangeLabel];
          return next;
        });

        if (activeRangeLabel === editOldRangeLabel) {
          setActiveRangeLabel(newLabel);
        }
      }

      setShowRangeForm(false);
      setEditOldRangeLabel("");
      setRangeMin("");
      setRangeMax("");
      toast.success(rangeFormMode === "add" ? "Range added." : "Range updated.");
    } catch (error) {
      toast.error(error?.message || `Failed to ${rangeFormMode === "add" ? "add" : "update"} range.`);
    }
  };

  const closeRangeForm = () => {
    setShowRangeForm(false);
    setEditOldRangeLabel("");
    setRangeMin("");
    setRangeMax("");
  };

  const onEditRangeClick = (event, label) => {
    event.stopPropagation();
    openEditRangeForm(label);
  };

  const onCardClick = (label) => {
    setActiveRangeLabel(label);
  };

  const beginAddMessage = () => {
    if (!activeRangeLabel) return;
    const newKey = `new-${activeRangeLabel}-${Date.now()}`;

    setEntriesByRange((prev) => ({
      ...prev,
      [activeRangeLabel]: [
        {
          _localKey: newKey,
          _isTemp: true,
          campusName: campusName || "",
          message: "",
          greeting: "",
        },
        ...(prev[activeRangeLabel] || []),
      ],
    }));

    setDrafts((prev) => ({
      ...prev,
      [newKey]: {
        mode: "add",
        rangeLabel: activeRangeLabel,
        campusName: campusName || "",
        message: "",
        greeting: "",
      },
    }));

    setEditingKey(newKey);
  };

  const handleSave = async (entry, selectedRangeLabel) => {
    const key = entry._localKey;
    const draft = drafts[key] || {
      mode: entry?._isTemp ? "add" : "edit",
      rangeLabel: selectedRangeLabel,
      campusName: entry.campusName || campusName || "",
      message: entry.message || "",
      greeting: entry.greeting || "",
    };

    const nextRange = String(draft.rangeLabel || selectedRangeLabel || "").trim();
    const nextCampus = String(draft.campusName || "").trim();
    const nextMessage = String(draft.message || "").trim();
    const nextGreeting = String(draft.greeting || "").trim();
    const saveMode = String(draft.mode || (entry?._isTemp ? "add" : "edit")).toLowerCase();
    const originalCampusName = String(draft.originalCampusName || "").trim();
    const originalMessage = String(draft.originalMessage || "").trim();
    const originalGreeting = String(draft.originalGreeting || "").trim();

    if (!nextRange || !nextCampus || !nextMessage || !nextGreeting) {
      toast.error("Range, Campus, Message and Greeting are required.");
      return;
    }

    setSavingKey(key);
    try {
      const ok = await ensureRangeExists(nextRange);
      if (!ok) {
        toast.error("Range does not exist and could not be created.");
        return;
      }

      const existingCampus = (entriesByRange[nextRange] || []).some(
        (row) => String(row?.campusName || "").trim().toLowerCase() === nextCampus.toLowerCase() &&
          row?._localKey !== entry?._localKey
      );

      // Add should not silently overwrite an existing campus entry.
      if (saveMode === "add" && existingCampus) {
        toast.error("Campus already exists in this range. Use edit to update it.");
        return;
      }

      // If edit changed campus, remove old campus row first to avoid duplicate rows.
      if (saveMode === "edit" && originalCampusName && originalCampusName.toLowerCase() !== nextCampus.toLowerCase()) {
        await deleteMessageInRange({
          variables: {
            input: {
              rangeLabel: nextRange,
              campusName: originalCampusName,
              message: originalMessage || undefined,
              greeting: originalGreeting || undefined,
            },
          },
        });
      }

      const { data } = await upsertMessageInRange({
        variables: {
          input: {
            rangeLabel: nextRange,
            campusName: nextCampus,
            message: nextMessage,
            greeting: nextGreeting,
          },
        },
      });

      const rangeFromMutation = (data?.upsertMessageInRange?.ranges || []).find((r) => r?.rangeLabel === nextRange);
      if (rangeFromMutation) {
        setEntriesByRange((prev) => ({
          ...prev,
          [nextRange]: toEntries(nextRange, rangeFromMutation.entries),
        }));
      } else {
        await loadRangeEntries(nextRange);
      }

      setActiveRangeLabel(nextRange);
      setEditingKey("");
      toast.success("Message saved.");
    } catch (error) {
      toast.error(error?.message || "Failed to save message.");
    } finally {
      setSavingKey("");
    }
  };

  const handleDelete = async (entry, rangeLabel) => {
    if (entry._isTemp) {
      setEntriesByRange((prev) => ({
        ...prev,
        [rangeLabel]: (prev[rangeLabel] || []).filter((e) => e._localKey !== entry._localKey),
      }));
      return;
    }

    setDeleteTarget({ entry, rangeLabel });
    setShowDeleteModal(true);
  };

  const confirmDeleteMessage = async () => {
    if (!deleteTarget?.entry || !deleteTarget?.rangeLabel) return;

    const { entry, rangeLabel } = deleteTarget;

    setDeletingKey(entry._localKey);
    try {
      const { data } = await deleteMessageInRange({
        variables: {
          input: {
            rangeLabel,
            campusName: entry.campusName,
            message: entry.message,
            greeting: entry.greeting,
          },
        },
      });

      const rangeFromMutation = (data?.deleteMessageInRange?.ranges || []).find((r) => r?.rangeLabel === rangeLabel);
      if (rangeFromMutation) {
        setEntriesByRange((prev) => ({
          ...prev,
          [rangeLabel]: toEntries(rangeLabel, rangeFromMutation.entries),
        }));
      } else {
        await loadRangeEntries(rangeLabel);
      }

      if (editingKey === entry._localKey) setEditingKey("");
      toast.success("Message deleted.");
    } catch (error) {
      toast.error(error?.message || "Failed to delete message.");
    } finally {
      setDeletingKey("");
      setDeleteTarget(null);
    }
  };

  const handleDeleteModalToggle = (isOpen) => {
    setShowDeleteModal(isOpen);
    if (!isOpen) {
      setDeleteTarget(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-3 md:p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => (activeRangeLabel ? setActiveRangeLabel("") : window.history.back())}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoMdArrowRoundBack className="h-6 w-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Report Message</h1>
          </div>

          {!activeRangeLabel ? (
            <button
              type="button"
              onClick={() => (showRangeForm ? closeRangeForm() : openAddRangeForm())}
              disabled={isBusy}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <IoMdAdd className="h-5 w-5" />
              Add Range
            </button>
          ) : null}
        </div>

        {!activeRangeLabel && showRangeForm ? (
          <div className="mb-3 inline-block max-w-xl rounded-xl border border-slate-200 bg-white p-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Min %"
                value={rangeMin}
                onChange={(e) => setRangeMin(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400"
              />
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Max %"
                value={rangeMax}
                onChange={(e) => setRangeMax(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSubmitRangeForm}
                  disabled={isBusy}
                  className="rounded-lg bg-[#0E3D5D] px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {rangeFormMode === "add" ? "Create" : "Update"}
                </button>
                <button
                  type="button"
                  onClick={closeRangeForm}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {!activeRangeLabel ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {rangeLabels.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => onCardClick(label)}
                className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                style={{ background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)" }}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h2 className="text-base font-bold text-slate-900">{label}</h2>
                  <button
                    type="button"
                    onClick={(event) => onEditRangeClick(event, label)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    aria-label={`Edit ${label}`}
                  >
                    <IoMdCreate className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-2 rounded-xl bg-white px-2.5 py-2">
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Campuses
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(() => {
                      const entries = entriesByRange[label] || [];
                      const campuses = Array.from(
                        new Set(
                          entries
                            .map((entry) => String(entry?.campusName || "").trim())
                            .filter(Boolean)
                        )
                      );
                      const list = campuses.length ? campuses : [campusName || "-"];
                      return list.map((name) => (
                        <span
                          key={`${label}-${name}`}
                          className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[12px] font-medium text-slate-700"
                        >
                          {name}
                        </span>
                      ));
                    })()}
                  </div>
                </div>
              </button>
            ))}

            {rangeLabels.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
                No ranges found. Use Add Range to create one.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900">{activeRangeLabel}</h2>
                <button
                  type="button"
                  onClick={beginAddMessage}
                  disabled={isBusy}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <IoMdAdd className="h-5 w-5" />
                  Add
                </button>
              </div>

              {activeEntries.length === 0 ? (
                <p className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3 text-sm text-slate-500">
                  No messages configured for this range.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {activeEntries.map((entry) => {
                    const itemKey = entry._localKey;
                    const isEditing = editingKey === itemKey;

                    return (
                      <div key={itemKey} className="rounded-xl border border-slate-100 bg-slate-50 p-1.5">
                        <div className="mb-0.5 flex items-center justify-end gap-2">
                          {!entry._isTemp ? (
                            <button
                              type="button"
                              aria-label={`Edit ${activeRangeLabel} message`}
                              onClick={() => startEdit(entry, activeRangeLabel)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                            >
                              <IoMdCreate className="h-4 w-4" />
                            </button>
                          ) : null}

                          <button
                            type="button"
                            aria-label={`Delete ${activeRangeLabel} message`}
                            onClick={() => handleDelete(entry, activeRangeLabel)}
                            disabled={isBusy || deletingKey === itemKey}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-red-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <IoMdTrash className="h-4 w-4" />
                          </button>
                        </div>

                        {isEditing ? (
                          <div className="space-y-1.5">
                            <div className="flex flex-col gap-2 md:flex-row">
                              <div className="w-full md:w-[70%]">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Greeting
                                </label>
                                <input
                                  type="text"
                                  value={drafts[itemKey]?.greeting ?? ""}
                                  onChange={(e) => handleDraftChange(itemKey, "greeting", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-800 outline-none focus:border-slate-400"
                                />
                              </div>

                              <div className="w-full md:w-[30%]">
                                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  Campus
                                </label>
                                <input
                                  type="text"
                                  value={drafts[itemKey]?.campusName ?? ""}
                                  onChange={(e) => handleDraftChange(itemKey, "campusName", e.target.value)}
                                  className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-800 outline-none focus:border-slate-400"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Message
                              </label>
                              <textarea
                                rows={3}
                                value={drafts[itemKey]?.message ?? ""}
                                onChange={(e) => handleDraftChange(itemKey, "message", e.target.value)}
                                className="w-full resize-none rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-800 outline-none focus:border-slate-400"
                              />
                            </div>

                            <div className="flex items-center gap-2 pt-0">
                              <button
                                type="button"
                                onClick={() => handleSave(entry, activeRangeLabel)}
                                disabled={isBusy || savingKey === itemKey}
                                className="rounded-lg bg-[#0E3D5D] px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {savingKey === itemKey ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelEdit(itemKey)}
                                disabled={isBusy || savingKey === itemKey}
                                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="flex flex-col gap-2 md:flex-row">
                              <div className="w-full md:w-[70%]">
                                <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Greeting</p>
                                <p className="rounded-lg border border-slate-100 bg-white px-3 py-1.5 text-base font-medium text-slate-800 whitespace-pre-wrap">
                                  {entry.greeting || "-"}
                                </p>
                              </div>

                              <div className="w-full md:w-[30%]">
                                <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Campus</p>
                                <p className="rounded-lg border border-slate-100 bg-white px-3 py-1.5 text-base font-medium text-slate-800 whitespace-pre-wrap">
                                  {String(entry.campusName || "").trim() || "-"}
                                </p>
                              </div>
                            </div>
                            <div>
                              <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Message</p>
                              <p className="min-h-[44px] rounded-lg border border-slate-100 bg-white px-3 py-1.5 text-base leading-6 text-slate-800 whitespace-pre-wrap">
                                {entry.message || "-"}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConformationModal
        openModal={showDeleteModal}
        setOpenModal={handleDeleteModalToggle}
        event={confirmDeleteMessage}
        text="Are you sure you want to delete this message?"
        size="3xl"
        bodyClassName="px-8"
      />
    </AdminLayout>
  );
};

export default ReportMessage;
